import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const PdfViewerModal = ({ formType, formId, title, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const blobUrlRef = useRef('');
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!formId || !formType) return;
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);

      try {
        const url =
          formType === 'vehicle-inspections'
            ? `${API}/vehicle-inspections/${formId}/pdf`
            : formType === 'signed-estimates'
            ? `${API}/sales/estimates/${formId}/signed-pdf`
            : formType === 'invoice'
            ? `${API}/sales/invoices/${formId}/pdf`
            : formType === 'receipt'
            ? `${API}/sales/invoices/${formId}/receipt`
            : `${API}/forms/${formType}/${formId}/pdf`;

        const response = await axios.get(url, {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (cancelled) return;

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = blobUrl;
        setPdfUrl(blobUrl);
      } catch (err) {
        console.error('Failed to load PDF:', err);
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
    };
  }, [formId, formType, token]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${title || 'document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenNewTab = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank');
  };

  return (
    <div
      className="fixed inset-0 flex flex-col bg-gray-900"
      style={{ zIndex: 9999 }}
      data-testid="pdf-viewer-modal"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 text-white shrink-0 border-b border-gray-700">
        <span className="text-sm font-medium truncate max-w-md">{title}</span>
        <div className="flex items-center gap-2 shrink-0">
          {pdfUrl && (
            <>
              <button
                onClick={handleOpenNewTab}
                className="text-gray-300 hover:text-white text-xs px-2.5 py-1.5 rounded hover:bg-gray-700 transition-colors border border-gray-600"
                data-testid="pdf-viewer-new-tab"
              >
                Open in New Tab
              </button>
              <button
                onClick={handleDownload}
                className="text-gray-300 hover:text-white text-xs px-2.5 py-1.5 rounded hover:bg-gray-700 transition-colors border border-gray-600"
                data-testid="pdf-viewer-download"
              >
                Download
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-gray-300 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-700 transition-colors"
            data-testid="pdf-viewer-close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 bg-gray-700">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white/60">
              <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm">Loading PDF...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-300 bg-gray-800 rounded-xl p-8 max-w-sm">
              <p className="font-semibold mb-1">Failed to load PDF</p>
              <p className="text-sm text-red-400 mb-3">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && pdfUrl && (
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-full h-full"
            data-testid="pdf-viewer-object"
          >
            <iframe
              src={pdfUrl}
              title={title || 'PDF Viewer'}
              className="w-full h-full border-0"
              data-testid="pdf-viewer-iframe"
            />
          </object>
        )}
      </div>
    </div>
  );
};

export default PdfViewerModal;
